import { action, computed, observable } from "mobx";
import { Observer } from "mobx-react";
import { Button } from "primereact/button";
import React, { useContext, useEffect } from "react";
import { QueryClient, useQueryClient } from "react-query";
import styled from "styled-components";
import { candidateAPI } from "../../../api/candidate";
import { GetDetailsForCandidateResponse } from "../../../api/candidate/getDetailsForCandidate";
import { getLocStore, useLocStore } from "../../../wsLocalization";
import { LocContext } from "../../../wsLocalization/LocContext";
import { Store2 } from "../../../WSUtils/Store";
import { useLatestQuery } from "../../../WSUtils/useLatestQuery";
import { UserInfo } from "../../../WSUtils/UserInfo";
import { UserInfoContext } from "../../../WSUtils/UserInfoContext";
import { useStore2 } from "../../../WSUtils/useStore";
import { WSFormGrid } from "../../../WSUtils/WSFormGrid";
import { sentryAssertData } from "../../../WSUtils/wsSentry";
import { CVEditContext, CVEditContextStore } from "../CVEditContext";
import {
  CVEditBaseField,
  cveField,
  OnRenderCvEditComponent,
} from "../Fields/CVEditBaseField";
import { defaultCvEditTextFieldDef } from "../Fields/defaultCvEditTextField";

export const CVEditContactSegment = () => {
  const L = useLocStore(LocContext.CV);
  const cvEditContext = useContext(CVEditContext);
  const userInfo = useContext(UserInfoContext);

  const store = useStore2(
    "CVEditContactSegmentStore",
    (dt) =>
      new CVEditContactSegmentStore({
        name: "CVEditContactSegmentStore",
        parentDt: dt,
        useHooks: (t: CVEditContactSegmentStore) => {
          t.userInfo = useContext(UserInfoContext);
          t.queryClient = useQueryClient();
          t.cvEditContext = useContext(CVEditContext);
        },
      })
  );

  const { data: details } = useLatestQuery(
    ["candidateAPI.getDetailsForCandidate"],
    () =>
      candidateAPI.getDetailsForCandidate({
        userInfo,
      })
  );

  sentryAssertData([details], { userInfo });

  useEffect(
    action(() => {
      store.details = details;
    }),
    [details]
  );

  return (
    <Observer
      render={() => (
        <CVEditContactSegmentStyles className="cve-contact-segment cve-segment">
          <div className="cvp-h2 cve-header">
            {L.id("c2de0ea287ab").loc("Kontakt")}
          </div>
          <CVEditBaseField def={store.emailField} />

          <CVEditBaseField def={store.phoneField} />

          <CVEditBaseField def={store.homePhoneField} />

          <CVEditBaseField def={store.workPhoneField} />
        </CVEditContactSegmentStyles>
      )}
    />
  );
};

const CVEditContactSegmentStyles = styled(WSFormGrid)`
  .cve-more-phones-button {
    flex-shrink: 0;
  }
`;

export class CVEditContactSegmentStore extends Store2<any> {
  declare userInfo: UserInfo;
  declare queryClient: QueryClient;
  declare cvEditContext: CVEditContextStore;

  @observable details: GetDetailsForCandidateResponse;

  getFieldDependencies = () => ({
    cvEditContext: this.cvEditContext,
    queryClient: this.queryClient,
    userInfo: this.userInfo,
    dt: this.dt,
  });

  @computed get L() {
    return getLocStore(this.userInfo, LocContext.CV);
  }

  @observable showAllPhoneNumbersHint = false;

  @computed get showAllPhoneNumbers() {
    if (this.showAllPhoneNumbersHint) return true;

    return (
      !!this.workPhoneField.store.value && !!this.homePhoneField.store.value
    );
  }

  @observable phoneField = cveField((getValueVersioner) => {
    const def = defaultCvEditTextFieldDef({
      getData: () => this.details,
      getValueVersioner,
      fieldName: "mobiltelefon",
      id: "Phone",
      label: "Mobilnummer",
      maxLength: 30,
      ...this.getFieldDependencies(),
    });

    const onRenderComponent: OnRenderCvEditComponent = ({
      defaultRender,
      def,
    }) => (
      <Observer
        render={() => {
          return (
            <>
              {!this.showAllPhoneNumbers && (
                <div className="p-inputgroup">
                  {defaultRender({ def })}
                  <Button
                    className="p-button-secondary cve-more-phones-button"
                    label={this.L.id("fb0396d5b7f6").loc("Flere")}
                    icon="pi pi-plus"
                    onClick={(evt) => {
                      evt.preventDefault();

                      this.showAllPhoneNumbersHint = true;

                      return false;
                    }}
                  />
                </div>
              )}

              {!!this.showAllPhoneNumbers && defaultRender({ def })}
            </>
          );
        }}
      />
    );

    return { ...def, onRenderComponent };
  });

  @observable homePhoneField = cveField((getValueVersioner) => {
    const def = defaultCvEditTextFieldDef({
      getData: () => this.details,
      getValueVersioner,
      fieldName: "privattelefon",
      id: "HomePhonme",
      label: "Fasttelefon",
      maxLength: 30,
      ...this.getFieldDependencies(),
    });

    return {
      ...def,
      onRenderContainer: (props) => (
        <Observer
          render={() => {
            const hideIt = !def.store.value && !this.showAllPhoneNumbers;

            return (
              <div
                {...props.containerProps}
                style={{
                  ...(props.containerProps?.style || {}),
                  display: hideIt ? "none" : undefined,
                }}
              >
                {props.children}
              </div>
            );
          }}
        />
      ),
    };
  });

  @observable workPhoneField = cveField((getValueVersioner) => {
    const def = defaultCvEditTextFieldDef({
      getData: () => this.details,
      getValueVersioner,
      fieldName: "arbeidstelefon",
      id: "WorkPhone",
      label: "Jobbtelefon",
      maxLength: 30,
      ...this.getFieldDependencies(),
    });

    return {
      ...def,
      onRenderContainer: (props) => (
        <Observer
          render={() => {
            const hideIt = !def.store.value && !this.showAllPhoneNumbers;

            return (
              <div
                {...props.containerProps}
                style={{
                  ...(props.containerProps?.style || {}),
                  display: hideIt ? "none" : undefined,
                }}
              >
                {props.children}
              </div>
            );
          }}
        />
      ),
    };
  });

  @observable emailField = cveField((getValueVersioner) => {
    const def = defaultCvEditTextFieldDef({
      getData: () => this.details,
      getValueVersioner,
      fieldName: "email",
      id: "Email",
      label: "E-post",
      maxLength: 100,
      required: true,
      valueValidation: (value: string) => {
        if (!value) return;

        if (!value.match(/^\S+@\S+\.\S+$/))
          return this.L.id("86a6a14ccde3").loc("Ugyldig e-post adresse");
      },
      ...this.getFieldDependencies(),
    });

    return def;
  });
}
